import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Edit2, Trash2, Clock, X, Upload, ChevronDown, ChevronRight, RotateCcw, SlidersHorizontal, Sparkles, Clipboard, Zap, Lightbulb, Rocket } from 'lucide-react';
import { pdfjs } from 'react-pdf';
import { useProjects } from '../contexts/ProjectsContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { resumableUpload, shouldUseResumableUpload } from '../lib/resumableUpload';
import { PDFDocument } from 'pdf-lib';
import { useTheme } from '../contexts/ThemeContext';
import { useBilling } from '../contexts/BillingContext';
import { getComponentColor } from '../utils/componentNameUtils';
import '../styles/HomePage.css';

type StatusFilterValue = 'estimating' | 'completed';
type HomePageStatusView = 'all' | 'estimating' | 'completed';

interface HomePageProps {
  statusView?: HomePageStatusView;
}

interface PostCreateLightingGateState {
  projectId: string;
  projectName: string;
}

interface CreateIndexingResult {
  didRunLightingScan: boolean;
  scheduleFound: boolean;
  schedulePagesCount: number | null;
  rawFixtureCount: number | null;
}

const STATUS_FILTER_OPTIONS: Array<{ value: StatusFilterValue; label: string }> = [
  { value: 'estimating', label: 'Estimating' },
  { value: 'completed', label: 'Completed' },
];

const getStatusFiltersForView = (statusView: HomePageStatusView): StatusFilterValue[] => {
  if (statusView === 'all') {
    return STATUS_FILTER_OPTIONS.map((option) => option.value);
  }
  if (statusView === 'completed') {
    return ['completed'];
  }
  return ['estimating'];
};

const LIGHTING_SETUP_GATE_ROLLOUT_AT_MS = Date.parse('2026-04-02T00:00:00.000Z');
const PROJECT_MANAGER_START_URL =
  process.env.REACT_APP_PROJECT_MANAGER_START_URL ||
  'https://project-manager-sryzkcx2eq-uc.a.run.app';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function HomePage({ statusView = 'estimating' }: HomePageProps) {
  const navigate = useNavigate();
  const { projects: allProjects, setProjects, updateProject } = useProjects();
  const { user, username } = useAuth();
  const { theme } = useTheme();
  const { billingGateLoading, hasActiveBilling } = useBilling();

  const [sortBy, setSortBy] = useState<'lastOpened' | 'name' | 'dateCreated'>('lastOpened');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showSortDropdown, setShowSortDropdown] = useState<boolean>(false);
  const [statusFilters, setStatusFilters] = useState<StatusFilterValue[]>(() => getStatusFiltersForView(statusView));

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [creatingProject, setCreatingProject] = useState<boolean>(false);
  const [newProjectName, setNewProjectName] = useState<string>('');
  const [newProjectFiles, setNewProjectFiles] = useState<File[]>([]);
  const [newProjectFileThumbnails, setNewProjectFileThumbnails] = useState<Record<string, string>>({});
  const [newSpecsFiles, setNewSpecsFiles] = useState<File[]>([]);
  const [isMergingPDFs, setIsMergingPDFs] = useState<boolean>(false);
  const [mergeProgress, setMergeProgress] = useState<number>(0);
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
  const [blueprintFiles, setBlueprintFiles] = useState<any[]>([]);
  const [specsFilesForManager, setSpecsFilesForManager] = useState<any[]>([]);
  const [projectFilesCache, setProjectFilesCache] = useState<Record<string, { blueprints: any[]; specs: any[] }>>({});
  const [fileNameEdits, setFileNameEdits] = useState<Record<string, string>>({});
  const [pendingDrawingFiles, setPendingDrawingFiles] = useState<Array<{ file: File; tempId: string }>>([]);
  const [pendingDrawingDeletions, setPendingDrawingDeletions] = useState<Set<string>>(new Set());
  const [pendingSpecsFiles, setPendingSpecsFiles] = useState<Array<{ file: File; tempId: string }>>([]);
  const [pendingSpecsDeletions, setPendingSpecsDeletions] = useState<Set<string>>(new Set());
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [editingProjectName, setEditingProjectName] = useState<string>('');
  const [editingProjectNameId, setEditingProjectNameId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showStatusPicker, setShowStatusPicker] = useState<string | null>(null);
  const [statusPickerRect, setStatusPickerRect] = useState<{ top: number; left: number } | null>(null);
  const [showRunningJobsSidebar, setShowRunningJobsSidebar] = useState<boolean>(true);
  const [etaNowMs, setEtaNowMs] = useState<number>(() => Date.now());
  const [postCreateLightingGate, setPostCreateLightingGate] = useState<PostCreateLightingGateState | null>(null);
  const [isResolvingPostCreateLightingGate, setIsResolvingPostCreateLightingGate] = useState<boolean>(false);
  const [postCreateLightingScheduleFile, setPostCreateLightingScheduleFile] = useState<File | null>(null);
  const [postCreateLightingGateError, setPostCreateLightingGateError] = useState<string | null>(null);
  const [isDraggingPostCreateLightingSchedule, setIsDraggingPostCreateLightingSchedule] = useState<boolean>(false);
  
  // Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadingFileName, setUploadingFileName] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  
  // Drag and drop states
  const [isDraggingDrawing, setIsDraggingDrawing] = useState<boolean>(false);
  const [isDraggingSpecs, setIsDraggingSpecs] = useState<boolean>(false);
  const [isDraggingModalDrawing, setIsDraggingModalDrawing] = useState<boolean>(false);
  const [isDraggingModalSpecs, setIsDraggingModalSpecs] = useState<boolean>(false);
  const newProjectFileThumbnailsRef = useRef<Record<string, string>>({});
  const activeDrawingFileKeysRef = useRef<Set<string>>(new Set());
  const drawingThumbnailInFlightRef = useRef<Set<string>>(new Set());

  const getDrawingFileKey = (file: File) => `${file.name}_${file.size}_${file.lastModified}`;

  const generateDrawingThumbnail = async (file: File): Promise<string | null> => {
    let pdfDocument: any = null;
    try {
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjs.getDocument({ data: new Uint8Array(arrayBuffer) });
      pdfDocument = await loadingTask.promise;
      const page = await pdfDocument.getPage(1);
      const viewport = page.getViewport({ scale: 0.22 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return null;

      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));

      await page.render({ canvasContext: context, viewport }).promise;
      const thumbnail = canvas.toDataURL('image/jpeg', 0.8);
      page.cleanup();
      return thumbnail;
    } catch (error) {
      console.warn(`Failed to generate thumbnail for ${file.name}:`, error);
      return null;
    } finally {
      if (pdfDocument?.destroy) {
        void pdfDocument.destroy();
      }
    }
  };

  useEffect(() => {
    if (!showStatusPicker) return;
    const handleClick = () => {
      setShowStatusPicker(null);
      setStatusPickerRect(null);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showStatusPicker]);

  useEffect(() => {
    if (!showSortDropdown) return;
    const handleClick = () => setShowSortDropdown(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showSortDropdown]);

  useEffect(() => {
    setStatusFilters(getStatusFiltersForView(statusView));
  }, [statusView]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setEtaNowMs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    newProjectFileThumbnailsRef.current = newProjectFileThumbnails;
  }, [newProjectFileThumbnails]);

  useEffect(() => {
    activeDrawingFileKeysRef.current = new Set(newProjectFiles.map((file) => getDrawingFileKey(file)));
  }, [newProjectFiles]);

  // Capitalize function
  const capitalizeWords = (str: string) => {
    return str.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Merge multiple PDFs into one
  const mergePDFs = async (files: File[]): Promise<File> => {
    console.log(`📄 Merging ${files.length} PDF files...`);
    const mergedPdf = await PDFDocument.create();
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log(`   Processing ${i + 1}/${files.length}: ${file.name}`);
      
      // Update progress: processing phase
      const loadProgress = ((i / files.length) * 80);
      setMergeProgress(loadProgress);
      
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      copiedPages.forEach((page) => mergedPdf.addPage(page));
      
      // Update progress after each file
      const processProgress = ((i + 1) / files.length) * 80;
      setMergeProgress(processProgress);
    }
    
    // Saving progress (final 20%)
    setMergeProgress(85);
    console.log('   Saving merged PDF...');
    const mergedPdfBytes = await mergedPdf.save();
    
    setMergeProgress(100);
    const mergedFile = new File([mergedPdfBytes], 'merged-drawings.pdf', { type: 'application/pdf' });
    console.log(`✅ Merged PDF created: ${(mergedFile.size / 1024 / 1024).toFixed(2)} MB`);
    return mergedFile;
  };

  const buildStandardProjectKey = () => {
    const S = 64;
    const CX = 32;
    const CY = 32;
    const R = 18;
    const SP = 7;
    const LW = 2;

    const makeCanvas = () => {
      const canvas = document.createElement('canvas');
      canvas.width = S;
      canvas.height = S;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Unable to create canvas context for standard key generation');
      }
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, S, S);
      ctx.strokeStyle = '#000';
      ctx.fillStyle = '#000';
      ctx.lineWidth = LW;
      return { canvas, ctx };
    };

    type TypeMod = 'duplex' | 'gfci' | 'quad' | 'dedicated' | 'halfswitch';

    const drawSpecialCircuitBackground = (
      ctx: CanvasRenderingContext2D,
      typeMod: TypeMod
    ) => {
      if (typeMod !== 'dedicated' && typeMod !== 'halfswitch') return;
      ctx.save();
      ctx.beginPath();
      if (typeMod === 'halfswitch') {
        ctx.arc(CX, CY, R, -Math.PI / 2, Math.PI / 2, false);
      } else {
        ctx.arc(CX, CY, R, 0, Math.PI * 2);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    const finishSpecialCircuitBars = (
      ctx: CanvasRenderingContext2D,
      typeMod: TypeMod,
      topY: number,
      bottomY: number
    ) => {
      if (typeMod !== 'dedicated' && typeMod !== 'halfswitch') return;
      ctx.fillStyle = '#fff';
      ctx.fillRect(CX - SP, CY - R, SP * 2, R * 2);
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.moveTo(CX - SP, topY);
      ctx.lineTo(CX - SP, bottomY);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(CX + SP, topY);
      ctx.lineTo(CX + SP, bottomY);
      ctx.stroke();
    };

    const makeWall = (typeMod: TypeMod) => {
      const { canvas, ctx } = makeCanvas();
      const wallY = S - 4;

      drawSpecialCircuitBackground(ctx, typeMod);

      ctx.lineWidth = LW;
      ctx.beginPath();
      ctx.moveTo(CX - SP, CY - R);
      ctx.lineTo(CX - SP, wallY);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(CX + SP, CY - R);
      ctx.lineTo(CX + SP, wallY);
      ctx.stroke();

      if (typeMod === 'gfci') {
        ctx.fillRect(CX - SP, CY - R, SP * 2, R * 2);
      }

      finishSpecialCircuitBars(ctx, typeMod, CY - R, wallY);

      ctx.lineWidth = LW;
      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, Math.PI * 2);
      ctx.stroke();

      if (typeMod === 'quad') {
        const cross = R + 4;
        ctx.beginPath();
        ctx.moveTo(CX - cross, CY - SP);
        ctx.lineTo(CX + cross, CY - SP);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(CX - cross, CY + SP);
        ctx.lineTo(CX + cross, CY + SP);
        ctx.stroke();
      }

      return canvas.toDataURL('image/png');
    };

    const makeCeiling = (typeMod: TypeMod) => {
      const { canvas, ctx } = makeCanvas();

      drawSpecialCircuitBackground(ctx, typeMod);

      ctx.lineWidth = LW;
      ctx.beginPath();
      ctx.moveTo(CX - SP, CY - R);
      ctx.lineTo(CX - SP, CY + R);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(CX + SP, CY - R);
      ctx.lineTo(CX + SP, CY + R);
      ctx.stroke();

      if (typeMod === 'gfci') {
        ctx.fillRect(CX - SP, CY - R, SP * 2, R * 2);
      }

      finishSpecialCircuitBars(ctx, typeMod, CY - R, CY + R);

      ctx.lineWidth = LW;
      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, Math.PI * 2);
      ctx.stroke();

      if (typeMod === 'quad') {
        ctx.beginPath();
        ctx.moveTo(CX - R, CY - SP);
        ctx.lineTo(CX + R, CY - SP);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(CX - R, CY + SP);
        ctx.lineTo(CX + R, CY + SP);
        ctx.stroke();
      }

      return canvas.toDataURL('image/png');
    };

    const makeFloor = (typeMod: TypeMod) => {
      const { canvas, ctx } = makeCanvas();
      const boxPad = 8;
      const boxR = R + boxPad;

      ctx.lineWidth = LW;
      ctx.strokeRect(CX - boxR, CY - boxR, boxR * 2, boxR * 2);

      drawSpecialCircuitBackground(ctx, typeMod);

      ctx.lineWidth = LW;
      ctx.beginPath();
      ctx.moveTo(CX - SP, CY - R);
      ctx.lineTo(CX - SP, CY + R);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(CX + SP, CY - R);
      ctx.lineTo(CX + SP, CY + R);
      ctx.stroke();

      if (typeMod === 'gfci') {
        ctx.fillRect(CX - SP, CY - R, SP * 2, R * 2);
      }

      finishSpecialCircuitBars(ctx, typeMod, CY - R, CY + R);

      ctx.lineWidth = LW;
      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, Math.PI * 2);
      ctx.stroke();

      if (typeMod === 'quad') {
        ctx.beginPath();
        ctx.moveTo(CX - R, CY - SP);
        ctx.lineTo(CX + R, CY - SP);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(CX - R, CY + SP);
        ctx.lineTo(CX + R, CY + SP);
        ctx.stroke();
      }

      return canvas.toDataURL('image/png');
    };

    const makeFurniture = () => {
      const { canvas, ctx } = makeCanvas();
      const boxSize = 28;
      ctx.lineWidth = LW;
      ctx.strokeRect(CX - boxSize / 2, CY - boxSize / 2, boxSize, boxSize);
      ctx.beginPath();
      ctx.moveTo(CX - SP, CY - boxSize / 2);
      ctx.lineTo(CX - SP, CY + boxSize / 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(CX + SP, CY - boxSize / 2);
      ctx.lineTo(CX + SP, CY + boxSize / 2);
      ctx.stroke();
      return canvas.toDataURL('image/png');
    };

    const makeJBox = () => {
      const { canvas, ctx } = makeCanvas();
      ctx.lineWidth = LW;
      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, Math.PI * 2);
      ctx.stroke();
      ctx.font = 'bold 20px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('J', CX, CY + 1);
      return canvas.toDataURL('image/png');
    };

    const makeTeleData = () => {
      const { canvas, ctx } = makeCanvas();
      ctx.lineWidth = LW;
      ctx.beginPath();
      ctx.moveTo(CX, 10);
      ctx.lineTo(54, 50);
      ctx.lineTo(10, 50);
      ctx.closePath();
      ctx.stroke();
      return canvas.toDataURL('image/png');
    };

    const makeSwitch = () => {
      const { canvas, ctx } = makeCanvas();
      ctx.font = 'bold 42px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('$', CX, CY + 2);
      return canvas.toDataURL('image/png');
    };

    const makeEntry = (name: string, componentType: string, referenceImage: string) => {
      const symbol = name;
      return {
        color: getComponentColor({
          name,
          symbol,
          componentType,
          symbolType: 'Power',
        }),
        name,
        symbol,
        componentType,
        symbolType: 'Power',
        description: 'Auto-generated standard class',
        subscript: '',
        pages: [],
        totalCount: 0,
        referenceImage,
        hasReferenceImage: true,
        isStandardClass: true,
      };
    };

    return {
      components: [
        makeEntry('Wall Mounted Duplex Receptacle', 'Receptacles', makeWall('duplex')),
        makeEntry('Wall Mounted GFCI Receptacle', 'Receptacles', makeWall('gfci')),
        makeEntry('Wall Mounted Dedicated Receptacle', 'Receptacles', makeWall('dedicated')),
        makeEntry(
          'Wall Mounted Halfswitch Specific Circuit Receptacle',
          'Receptacles',
          makeWall('halfswitch')
        ),
        makeEntry('Wall Mounted Quadruplex Receptacle', 'Receptacles', makeWall('quad')),
        makeEntry('Ceiling Mounted Duplex Receptacle', 'Receptacles', makeCeiling('duplex')),
        makeEntry('Ceiling Mounted GFCI Receptacle', 'Receptacles', makeCeiling('gfci')),
        makeEntry(
          'Ceiling Mounted Dedicated Receptacle',
          'Receptacles',
          makeCeiling('dedicated')
        ),
        makeEntry(
          'Ceiling Mounted Halfswitch Specific Circuit Receptacle',
          'Receptacles',
          makeCeiling('halfswitch')
        ),
        makeEntry(
          'Ceiling Mounted Quadruplex Receptacle',
          'Receptacles',
          makeCeiling('quad')
        ),
        makeEntry('Floor Mounted Duplex Receptacle', 'Receptacles', makeFloor('duplex')),
        makeEntry('Floor Mounted GFCI Receptacle', 'Receptacles', makeFloor('gfci')),
        makeEntry('Floor Mounted Dedicated Receptacle', 'Receptacles', makeFloor('dedicated')),
        makeEntry(
          'Floor Mounted Halfswitch Specific Circuit Receptacle',
          'Receptacles',
          makeFloor('halfswitch')
        ),
        makeEntry('Floor Mounted Quadruplex Receptacle', 'Receptacles', makeFloor('quad')),
        makeEntry('Furniture Mounted Duplex Receptacle', 'Receptacles', makeFurniture()),
        makeEntry('Furniture Mounted GFCI Receptacle', 'Receptacles', makeFurniture()),
        makeEntry(
          'Furniture Mounted Dedicated Receptacle',
          'Receptacles',
          makeFurniture()
        ),
        makeEntry(
          'Furniture Mounted Halfswitch Specific Circuit Receptacle',
          'Receptacles',
          makeFurniture()
        ),
        makeEntry('Furniture Mounted Quadruplex Receptacle', 'Receptacles', makeFurniture()),
        makeEntry('Switch', 'Switches', makeSwitch()),
        makeEntry('Junction Box', 'Distribution Equipment', makeJBox()),
        makeEntry('Tele/Data Outlet', 'Telecom', makeTeleData()),
      ],
      extractedAt: new Date().toISOString(),
      pdfHash: '',
    };
  };

  const seedStandardProjectKeyForProject = async (estimationId: string) => {
    const standardProjectKey = buildStandardProjectKey();
    const { data, error } = await supabase
      .from('estimations')
      .update({ project_key: standardProjectKey })
      .eq('id', estimationId)
      .select('project_key')
      .single();

    if (error) throw error;

    const seededComponentCount = Array.isArray(data?.project_key?.components)
      ? data.project_key.components.length
      : 0;
    if (seededComponentCount === 0) {
      throw new Error('Standard key seeding produced zero components');
    }
    return seededComponentCount;
  };

  const statusOptions = [
    { value: 'estimating', label: 'Estimating', color: 'var(--yellow-text-dark)', bgColor: '#fef3c7' },
    { value: 'completed', label: 'Completed', color: 'var(--green-text-dark)', bgColor: 'var(--green-tint-strong)' },
  ];

  const legacyInReviewStatusOption = {
    value: 'in-review',
    label: 'In Review (Legacy)',
    color: 'var(--purple-text-dark)',
    bgColor: '#e9d5ff',
  };

  const getStatusOption = (status: string) => {
    if (status === 'in-review') {
      return legacyInReviewStatusOption;
    }
    return statusOptions.find(opt => opt.value === status) || statusOptions[0];
  };

const splitFileName = (fileName: string) => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) {
    return { base: fileName, ext: '' };
  }
  return {
    base: fileName.slice(0, lastDot),
    ext: fileName.slice(lastDot),
  };
};

  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

const buildFileNameEditMap = (blueprints: any[] = [], specs: any[] = []) => {
  const edits: Record<string, string> = {};
  blueprints.forEach((file) => {
    const { base } = splitFileName(file.file_name);
    edits[file.id] = base;
  });
  specs.forEach((file) => {
    const { base } = splitFileName(file.file_name);
    edits[file.id] = base;
  });
  return edits;
};

const getEditedFileName = (fileId: string, originalName: string, edits: Record<string, string>) => {
  const { base, ext } = splitFileName(originalName);
  const editedBase = (edits[fileId] ?? base).trim() || base;
  return `${editedBase}${ext}`;
};

const isHttpUrl = (value?: string) => /^https?:\/\//i.test(String(value || '').trim());

  const handleBillingLockedAction = () => {
    alert('You need to connect a card first before creating or opening projects.');
    navigate('/settings/billing');
  };

  const deleteProjectWithStorageCleanup = async (projectId: string) => {
    if (!supabase) return;

    const [drawingPathsResult, specsPathsResult] = await Promise.all([
      supabase
        .from('estimation_files')
        .select('storage_path')
        .eq('estimation_id', projectId),
      supabase
        .from('estimation_specs')
        .select('storage_path')
        .eq('estimation_id', projectId),
    ]);

    const allPaths = [
      ...(drawingPathsResult.data || []).map((row: any) => String(row?.storage_path || '').trim()),
      ...(specsPathsResult.data || []).map((row: any) => String(row?.storage_path || '').trim()),
    ].filter((path) => path.length > 0 && !isHttpUrl(path));

    if (allPaths.length > 0) {
      const uniquePaths = Array.from(new Set(allPaths));
      const { error: storageDeleteError } = await supabase.storage.from('bucket').remove(uniquePaths);
      if (storageDeleteError) {
        console.warn('⚠️ Failed to delete one or more storage files during project cleanup:', storageDeleteError);
      }
    }

    const { error: deleteError } = await supabase
      .from('estimations')
      .delete()
      .eq('id', projectId);

    if (deleteError) {
      throw deleteError;
    }
  };

  const resolveDrawingFileUri = async (storagePath: string): Promise<string> => {
    if (!supabase) return '';
    const normalizedStoragePath = String(storagePath || '').trim();
    if (!normalizedStoragePath) return '';

    let fileUri = '';
    try {
      const { data, error } = await supabase.storage
        .from('bucket')
        .createSignedUrl(normalizedStoragePath, 60 * 10);
      if (!error && data?.signedUrl) {
        fileUri = data.signedUrl;
      }
    } catch (error) {
      console.warn('⚠️ Failed to create signed URL for drawing scan:', error);
    }

    if (!fileUri) {
      fileUri = supabase.storage.from('bucket').getPublicUrl(normalizedStoragePath).data.publicUrl;
    }

    return fileUri;
  };

  const runCreateTimeIndexing = async (projectId: string, drawingStoragePath: string): Promise<CreateIndexingResult | null> => {
    if (!supabase) return null;

    const storagePath = String(drawingStoragePath || '').trim();
    if (!storagePath) return null;

    const fileUri = await resolveDrawingFileUri(storagePath);
    if (!fileUri) {
      throw new Error('Unable to resolve drawing URL for create-time indexing');
    }

    const response = await fetch('/api/index-drawing-pages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        estimationId: projectId,
        fileUri,
        storagePath,
      }),
    });

    if (!response.ok) {
      throw new Error(`Create-time indexing failed (${response.status})`);
    }

    const data = await response.json();
    return {
      didRunLightingScan: data?.indexedLightingAppliedToKey === true,
      scheduleFound: data?.indexedLightingScheduleFound === true,
      schedulePagesCount: Number.isFinite(data?.indexedLightingSchedulePagesCount)
        ? Number(data.indexedLightingSchedulePagesCount)
        : null,
      rawFixtureCount: Number.isFinite(data?.indexedLightingRawFixtureCount)
        ? Number(data.indexedLightingRawFixtureCount)
        : null,
    };
  };

  const runLightingOnlyScheduleRecovery = async (projectId: string, drawingStoragePath: string): Promise<CreateIndexingResult | null> => {
    if (!supabase) return null;

    const storagePath = String(drawingStoragePath || '').trim();
    if (!storagePath) return null;

    const fileUri = await resolveDrawingFileUri(storagePath);
    if (!fileUri) {
      throw new Error('Unable to resolve drawing URL for lighting-only recovery scan');
    }

    const response = await fetch('/api/index-drawing-pages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        estimationId: projectId,
        fileUri,
        storagePath,
        force: true,
        lightingOnly: true,
      }),
    });

    if (!response.ok) {
      throw new Error(`Lighting-only recovery scan failed (${response.status})`);
    }

    const data = await response.json();
    return {
      didRunLightingScan: data?.indexedLightingAppliedToKey === true,
      scheduleFound: data?.indexedLightingScheduleFound === true,
      schedulePagesCount: Number.isFinite(data?.indexedLightingSchedulePagesCount)
        ? Number(data.indexedLightingSchedulePagesCount)
        : null,
      rawFixtureCount: Number.isFinite(data?.indexedLightingRawFixtureCount)
        ? Number(data.indexedLightingRawFixtureCount)
        : null,
    };
  };

  const createDisabled = !hasActiveBilling;
  const createTooltip = hasActiveBilling
    ? 'Create New'
    : 'You need to connect a card first';

  const isProjectLocked = (project: any) => {
    const runState = String(project?.runState || '').toLowerCase();
    return runState === 'queued' || runState === 'running';
  };

  const isProjectFreshlyDone = (project: any) => {
    const runState = String(project?.runState || '').toLowerCase();
    if (runState !== 'done') return false;
    const runUpdatedMs = Date.parse(String(project?.runUpdatedAt || ''));
    if (!Number.isFinite(runUpdatedMs)) return false;
    const lastOpenedMs = Date.parse(String(project?.lastOpenedAt || ''));
    if (!Number.isFinite(lastOpenedMs)) return true;
    return lastOpenedMs < runUpdatedMs;
  };

  const formatRunStage = (runStage?: string) => {
    const normalized = String(runStage || 'running').trim().toLowerCase();
    if (!normalized || normalized === 'none') return 'Running';
    if (normalized === 'indexing') return 'Labeling pages';
    if (normalized === 'counting_power') return 'Counting power';
    if (normalized === 'counting_lighting') return 'Counting lighting';
    if (normalized === 'save_annotations') return 'Saving annotations';
    return normalized
      .split('_')
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  };

  const isLabelingStage = (runStage?: string) =>
    String(runStage || '').trim().toLowerCase() === 'indexing';

  const getRunStageVisual = (runStage?: string) => {
    const normalized = String(runStage || '').trim().toLowerCase();
    if (normalized === 'indexing') return 'labeling';
    if (normalized === 'counting_power') return 'power';
    if (normalized === 'counting_lighting') return 'lighting';
    return 'default';
  };

  const renderRunStageBadge = (runStage?: string) => {
    const visual = getRunStageVisual(runStage);
    const label = formatRunStage(runStage);
    const IconComponent =
      visual === 'labeling' ? Clipboard : visual === 'power' ? Zap : visual === 'lighting' ? Lightbulb : null;

    return (
      <span className={`running-stage-chip running-stage-${visual}`}>
        {IconComponent && (
          <span className="running-stage-icon" aria-hidden="true">
            <IconComponent size={12} strokeWidth={2.1} />
          </span>
        )}
        <span className="running-stage-text">{label}</span>
      </span>
    );
  };

  const getLiveTimeLeft = (project: any) => {
    const baseTimeLeft = Number(project?.timeLeft);
    const normalizedBase = Number.isFinite(baseTimeLeft)
      ? Math.max(0, Math.floor(baseTimeLeft))
      : 0;
    if (!isProjectLocked(project)) return normalizedBase;

    const updatedAtMs = Date.parse(String(project?.runUpdatedAt || ''));
    if (!Number.isFinite(updatedAtMs)) return normalizedBase;

    const elapsedSeconds = Math.max(0, Math.floor((etaNowMs - updatedAtMs) / 1000));
    return Math.max(0, normalizedBase - elapsedSeconds);
  };

  const formatTimeLeft = (timeLeft?: number) => {
    const totalSeconds = Number.isFinite(Number(timeLeft))
      ? Math.max(0, Math.floor(Number(timeLeft)))
      : 0;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const launchProjectManagerJob = async ({
    estimationId,
    userId,
    drawingStoragePath,
  }: {
    estimationId: string;
    userId: string;
    drawingStoragePath: string;
  }) => {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }

    const { data: billingProfile, error: billingError } = await supabase
      .from('billing_profiles')
      .select('id, stripe_customer_id, status')
      .eq('user_id', userId)
      .maybeSingle();

    if (billingError) throw billingError;
    if (!billingProfile?.id || !billingProfile?.stripe_customer_id) {
      throw new Error('Missing billing profile. Please connect billing before creating projects.');
    }

    const response = await fetch(PROJECT_MANAGER_START_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        estimationId,
        userId,
        billingProfile: {
          billingProfileId: billingProfile.id,
          stripeCustomerId: billingProfile.stripe_customer_id,
          status: billingProfile.status || 'active',
        },
        drawing: {
          storagePath: drawingStoragePath,
        },
        options: {
          forceReindex: false,
          notifyOnStart: true,
        },
      }),
    });

    const payload = await response.json().catch(() => ({}));
    return { response, payload };
  };

  const displayProjects = useMemo(() => {
    let filtered = allProjects;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = allProjects.filter(project =>
        project.name.toLowerCase().includes(query)
      );
    }

    const hasStatusFilter =
      statusFilters.length > 0 && statusFilters.length < STATUS_FILTER_OPTIONS.length;
    if (hasStatusFilter) {
      filtered = filtered.filter((project) => {
        let normalizedStatus: StatusFilterValue | null = null;
        if (project.status === 'estimating') normalizedStatus = 'estimating';
        if (project.status === 'completed' || project.status === 'accepted') normalizedStatus = 'completed';
        return normalizedStatus ? statusFilters.includes(normalizedStatus) : false;
      });
    }

    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (sortBy === 'dateCreated') {
        cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      } else {
        cmp = new Date(a.lastOpenedAt || a.updatedAt).getTime() - new Date(b.lastOpenedAt || b.updatedAt).getTime();
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [allProjects, searchQuery, sortBy, sortDirection, statusFilters]);

  const runningJobs = useMemo(
    () => allProjects.filter((project) => isProjectLocked(project)),
    [allProjects]
  );

  const handleDelete = async (projectId: string, projectName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${projectName}"?`)) return;

    try {
      if (supabase) {
        await deleteProjectWithStorageCleanup(projectId);
      }
      setProjects((prev) => prev.filter((project) => project.id !== projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project');
    }
  };

  const handleCreateProject = async () => {
    if (billingGateLoading || !hasActiveBilling) {
      handleBillingLockedAction();
      return;
    }

    if (!newProjectName.trim()) {
      alert('Please provide a project name');
      return;
    }

    if (newProjectFiles.length === 0) {
      alert('Please upload at least one drawing PDF before creating the project.');
      return;
    }

    setCreatingProject(true);

    try {
      if (!supabase) {
        throw new Error('Supabase not configured');
      }
      if (!user?.id) {
        throw new Error('User session not found');
      }

      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(7);
      let primaryDrawingStoragePath = '';

      // Create project in estimations table
      const { data: projectData, error: projectError } = await supabase
        .from('estimations')
        .insert({
          name: capitalizeWords(newProjectName.trim()),
          client_name: 'New Client',
          project_type: 'commercial',
          status: 'estimating',
          folder_id: '00000000-0000-0000-0000-000000000001',
          username: username,
          user_id: user?.id, // Required for RLS - links estimation to authenticated user
        })
        .select()
        .single();

      if (projectError) {
        throw projectError;
      }

      const seededComponentCount = await seedStandardProjectKeyForProject(projectData.id);
      console.log(
        `⚡ Seeded ${seededComponentCount} standard key components for new project ${projectData.id}`
      );

      // Upload drawing PDF if provided (merge multiple files if needed)
      if (newProjectFiles.length > 0) {
        let fileToUpload: File;
        
        if (newProjectFiles.length > 1) {
          // Merge multiple PDFs into one
          setIsMergingPDFs(true);
          setMergeProgress(0);
          console.log(`🔄 Merging ${newProjectFiles.length} PDF files...`);
          fileToUpload = await mergePDFs(newProjectFiles);
          setIsMergingPDFs(false);
          setMergeProgress(0);
        } else {
          fileToUpload = newProjectFiles[0];
        }
        
        console.log(`🔍 DEBUG: Uploading blueprint ${fileToUpload.name}, size: ${(fileToUpload.size / 1024 / 1024).toFixed(2)} MB`);
        
        setIsUploading(true);
        setUploadingFileName(fileToUpload.name);
        setUploadProgress(0);
        
        const filePath = `blueprints/${timestamp}_${randomStr}.pdf`;
        primaryDrawingStoragePath = filePath;
        let uploadSuccess = false;
        
        if (shouldUseResumableUpload(fileToUpload.size)) {
          console.log('📤 Using TUS resumable upload for large file...');
          const result = await resumableUpload({
            bucketName: 'bucket',
            filePath,
            file: fileToUpload,
            onProgress: (progress) => {
              setUploadProgress(progress.percentage);
              console.log(`   Upload progress: ${progress.percentage}%`);
            },
            onError: (error) => {
              console.error(`   Upload error:`, error);
              setIsUploading(false);
            }
          });
          uploadSuccess = result.success;
          if (!uploadSuccess && result.error) {
            setIsUploading(false);
            throw result.error;
          }
        } else {
          console.log('📤 Using standard upload for small file...');
          const { error: uploadError } = await supabase.storage
            .from('bucket')
            .upload(filePath, fileToUpload);

          if (uploadError) {
            setIsUploading(false);
            throw uploadError;
          }
          uploadSuccess = true;
        }
        
        setIsUploading(false);
        setUploadProgress(100);
        
        if (!uploadSuccess) {
          throw new Error('Upload failed');
      }

      // Add drawing file to estimation_files table
      const { error: fileError } = await supabase
        .from('estimation_files')
        .insert({
          estimation_id: projectData.id,
          file_name: fileToUpload.name,
          file_size: fileToUpload.size,
          file_type: 'application/pdf',
          storage_path: filePath,
          file_order: 0,
        });

      if (fileError) {
        throw fileError;
        }
      }

      // Upload specs files if provided (kept as separate files in estimation_specs)
      if (newSpecsFiles.length > 0) {
        console.log(`📤 Uploading ${newSpecsFiles.length} specification file(s)...`);

        for (let i = 0; i < newSpecsFiles.length; i++) {
          const specFile = newSpecsFiles[i];
          const { ext } = splitFileName(specFile.name);
          const specPath = `specs/${Date.now()}_${i}_${Math.random().toString(36).substring(7)}${ext || ''}`;

          setIsUploading(true);
          setUploadingFileName(specFile.name);
          setUploadProgress(0);

          let specUploadSuccess = false;

          if (shouldUseResumableUpload(specFile.size)) {
            const result = await resumableUpload({
              bucketName: 'bucket',
              filePath: specPath,
              file: specFile,
              onProgress: (progress) => {
                setUploadProgress(progress.percentage);
              },
              onError: (error) => {
                console.error(`Spec upload error for ${specFile.name}:`, error);
              }
            });

            specUploadSuccess = result.success;
            if (!specUploadSuccess && result.error) {
              console.error(`Spec upload failed for ${specFile.name}:`, result.error);
            }
          } else {
            const { error: specUploadError } = await supabase.storage
              .from('bucket')
              .upload(specPath, specFile);

            if (specUploadError) {
              console.error(`Spec upload error for ${specFile.name}:`, specUploadError);
            } else {
              specUploadSuccess = true;
              setUploadProgress(100);
            }
          }

          setIsUploading(false);

          if (!specUploadSuccess) {
            continue;
          }

          const { error: specInsertError } = await supabase
            .from('estimation_specs')
            .insert({
              estimation_id: projectData.id,
              file_name: specFile.name,
              file_size: specFile.size,
              file_type: specFile.type || 'application/octet-stream',
              storage_path: specPath,
              file_order: i,
            });

          if (specInsertError) {
            console.error(`Error inserting spec record for ${specFile.name}:`, specInsertError);
          }
        }

        setUploadProgress(0);
        setUploadingFileName('');
      }

      if (!primaryDrawingStoragePath) {
        throw new Error('Missing drawing storage path for project manager launch');
      }

      const { response, payload } = await launchProjectManagerJob({
        estimationId: projectData.id,
        userId: user.id,
        drawingStoragePath: primaryDrawingStoragePath,
      });

      if (response.status !== 202 && response.status !== 409) {
        throw new Error(
          payload?.message ||
            payload?.error ||
            `Failed to launch project manager (${response.status})`
        );
      }

      if (response.status === 409) {
        console.warn('Project manager already has an active job for this project:', payload);
      }

      setShowRunningJobsSidebar(true);
      handleCancelCreate();
      window.dispatchEvent(new Event('refetch-projects'));
    } catch (error) {
      console.error('Error creating project:', error);
      alert(
        error instanceof Error
          ? error.message
          : 'Failed to create project. Please try again.'
      );
    } finally {
      setCreatingProject(false);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadingFileName('');
      setIsMergingPDFs(false);
      setMergeProgress(0);
    }
  };

  const handleCancelCreate = () => {
    setNewProjectName('');
    setNewProjectFiles([]);
    setNewProjectFileThumbnails({});
    setNewSpecsFiles([]);
    activeDrawingFileKeysRef.current.clear();
    drawingThumbnailInFlightRef.current.clear();
    setShowCreateForm(false);
    setShowCreateModal(false);
    setIsMergingPDFs(false);
    setMergeProgress(0);
  };

  const handleSpecsFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      // Check for duplicates
      const existingNames = newSpecsFiles.map(f => f.name.toLowerCase());
      const duplicates: string[] = [];
      const uniqueFiles = newFiles.filter(file => {
        if (existingNames.includes(file.name.toLowerCase())) {
          duplicates.push(file.name);
          return false;
        }
        return true;
      });
      
      if (duplicates.length > 0) {
        alert(`The following files already exist and were skipped:\n${duplicates.join('\n')}`);
      }
      
      setNewSpecsFiles(prev => [...prev, ...uniqueFiles]);
    }
  };

  const removeSpecsFile = (index: number) => {
    setNewSpecsFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeDrawingFile = (index: number) => {
    const removedFile = newProjectFiles[index];
    if (removedFile) {
      const fileKey = getDrawingFileKey(removedFile);
      activeDrawingFileKeysRef.current.delete(fileKey);
      drawingThumbnailInFlightRef.current.delete(fileKey);
      setNewProjectFileThumbnails((prev) => {
        if (!prev[fileKey]) return prev;
        const next = { ...prev };
        delete next[fileKey];
        return next;
      });
    }
    setNewProjectFiles(prev => prev.filter((_, i) => i !== index));
  };

  const addDrawingFiles = (files: File[]) => {
    if (files.length === 0) return;

    const existingNames = newProjectFiles.map((f) => f.name.toLowerCase());
    const duplicates: string[] = [];
    const uniqueFiles = files.filter((file) => {
      const normalizedName = file.name.toLowerCase();
      if (existingNames.includes(normalizedName)) {
        duplicates.push(file.name);
        return false;
      }
      existingNames.push(normalizedName);
      return true;
    });

    if (duplicates.length > 0) {
      alert(`The following files already exist and were skipped:\n${duplicates.join('\n')}`);
    }

    if (uniqueFiles.length === 0) return;

    setNewProjectFiles((prev) => [...prev, ...uniqueFiles]);

    uniqueFiles.forEach((file) => {
      const fileKey = getDrawingFileKey(file);
      activeDrawingFileKeysRef.current.add(fileKey);

      if (newProjectFileThumbnailsRef.current[fileKey]) return;
      if (drawingThumbnailInFlightRef.current.has(fileKey)) return;

      drawingThumbnailInFlightRef.current.add(fileKey);
      void (async () => {
        try {
          const thumbnail = await generateDrawingThumbnail(file);
          if (!thumbnail) return;
          if (!activeDrawingFileKeysRef.current.has(fileKey)) return;

          setNewProjectFileThumbnails((prev) => {
            if (prev[fileKey]) return prev;
            return { ...prev, [fileKey]: thumbnail };
          });
        } finally {
          drawingThumbnailInFlightRef.current.delete(fileKey);
        }
      })();
    });
  };

  const fetchProjectFiles = async (projectId: string) => {
    if (!supabase) return { blueprints: [], specs: [] };
    const [bpResult, specsResult] = await Promise.all([
      supabase
        .from('estimation_files')
        .select('*')
        .eq('estimation_id', projectId)
        .order('file_order', { ascending: true }),
      supabase
        .from('estimation_specs')
        .select('*')
        .eq('estimation_id', projectId)
        .order('file_order', { ascending: true })
    ]);

    return {
      blueprints: bpResult.data || [],
      specs: specsResult.data || []
    };
  };

  const openMissingLightingScheduleGate = (projectId: string, projectName?: string) => {
    setPostCreateLightingScheduleFile(null);
    setPostCreateLightingGateError(null);
    setIsDraggingPostCreateLightingSchedule(false);
    setPostCreateLightingGate({
      projectId,
      projectName: projectName || 'Project',
    });
  };

  const handleOpenProject = async (projectId: string) => {
    if (billingGateLoading || !hasActiveBilling) {
      handleBillingLockedAction();
      return;
    }

    const project = allProjects.find((p) => p.id === projectId);
    if (project && isProjectLocked(project)) {
      alert('This project is currently processing. Please wait for it to finish.');
      return;
    }

    try {
      if (supabase) {
        await supabase
          .from('estimations')
          .update({ last_opened_at: new Date().toISOString() })
          .eq('id', projectId);
      }
    } catch (error) {
      console.error('Error updating last_opened_at:', error);
    }
    navigate(`/estimation/${projectId}`);
    setSearchQuery('');
  };

  const setPostCreateLightingScheduleFromFiles = (files: File[]) => {
    if (!files || files.length === 0) return;

    const pdfFiles = files.filter(
      (file) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );
    if (pdfFiles.length === 0) {
      setPostCreateLightingGateError('Please upload a PDF that includes the lighting fixture schedule.');
      return;
    }

    if (files.length > 1 || pdfFiles.length > 1) {
      alert('Only one lighting schedule PDF can be uploaded at a time. We selected the first valid PDF.');
    }

    setPostCreateLightingScheduleFile(pdfFiles[0]);
    setPostCreateLightingGateError(null);
  };

  const handlePostCreateLightingScheduleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setPostCreateLightingScheduleFromFiles(files);
    e.target.value = '';
  };

  const handlePostCreateLightingScheduleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPostCreateLightingSchedule(true);
  };

  const handlePostCreateLightingScheduleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPostCreateLightingSchedule(false);
  };

  const handlePostCreateLightingScheduleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPostCreateLightingSchedule(false);
    const files = Array.from(e.dataTransfer.files || []);
    setPostCreateLightingScheduleFromFiles(files);
  };

  const handleScanPostCreateLightingSchedule = async () => {
    if (!postCreateLightingGate || !supabase) return;
    if (!postCreateLightingScheduleFile) {
      setPostCreateLightingGateError('Upload a PDF that includes the lighting fixture schedule first.');
      return;
    }

    setIsResolvingPostCreateLightingGate(true);
    setPostCreateLightingGateError(null);

    const projectId = postCreateLightingGate.projectId;
    let tempStoragePath = '';

    try {
      const tempExt = '.pdf';
      tempStoragePath = `lighting-schedule-scans/${projectId}/${Date.now()}_${Math.random().toString(36).substring(7)}${tempExt}`;

      let uploadSuccess = false;
      if (shouldUseResumableUpload(postCreateLightingScheduleFile.size)) {
        const result = await resumableUpload({
          bucketName: 'bucket',
          filePath: tempStoragePath,
          file: postCreateLightingScheduleFile,
          onProgress: () => {},
          onError: (error) => {
            console.error('Lighting schedule upload error:', error);
          }
        });
        uploadSuccess = result.success;
        if (!uploadSuccess && result.error) {
          throw result.error;
        }
      } else {
        const { error: uploadError } = await supabase.storage
          .from('bucket')
          .upload(tempStoragePath, postCreateLightingScheduleFile);
        if (uploadError) throw uploadError;
        uploadSuccess = true;
      }

      if (!uploadSuccess) {
        throw new Error('Failed to upload lighting schedule PDF for scanning.');
      }

      let scanFileUri = '';
      try {
        const { data: signedData, error: signedError } = await supabase.storage
          .from('bucket')
          .createSignedUrl(tempStoragePath, 60 * 10);
        if (!signedError && signedData?.signedUrl) {
          scanFileUri = signedData.signedUrl;
        }
      } catch (signedError) {
        console.warn('Failed to create signed URL for lighting-only scan upload:', signedError);
      }

      if (!scanFileUri) {
        scanFileUri = supabase.storage.from('bucket').getPublicUrl(tempStoragePath).data.publicUrl;
      }

      const response = await fetch('/api/index-drawing-pages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          estimationId: projectId,
          fileUri: scanFileUri,
          storagePath: tempStoragePath,
          force: true,
          lightingOnly: true,
        }),
      });

      const scanPayload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(scanPayload?.error || `Lighting-only scan failed (${response.status})`);
      }

      const rawFixtureCount = Number.isFinite(scanPayload?.indexedLightingRawFixtureCount)
        ? Number(scanPayload.indexedLightingRawFixtureCount)
        : null;
      const scheduleDetected = typeof rawFixtureCount === 'number'
        ? rawFixtureCount > 0
        : scanPayload?.indexedLightingScheduleFound === true;

      if (!scheduleDetected) {
        setPostCreateLightingGateError('No fixture schedule rows were detected. Upload a PDF page that clearly includes the lighting fixture schedule.');
        return;
      }

      const { error: gateReadyError } = await supabase
        .from('estimations')
        .update({
          setup_gate_status: 'ready',
          takeoff_scope: 'full',
        })
        .eq('id', projectId);
      if (gateReadyError) throw gateReadyError;

      setProjects((prev) => prev.map((project) => (
        project.id === projectId
          ? { ...project, setupGateStatus: 'ready', takeoffScope: 'full' }
          : project
      )));

      setPostCreateLightingGate(null);
      setPostCreateLightingScheduleFile(null);
      setPostCreateLightingGateError(null);
      setIsDraggingPostCreateLightingSchedule(false);
      navigate(`/estimation/${projectId}`);
      setSearchQuery('');
    } catch (error: any) {
      console.error('Failed lighting-only recovery scan:', error);
      setPostCreateLightingGateError(error?.message || 'Failed to scan lighting schedule PDF. Please try again.');
    } finally {
      if (tempStoragePath) {
        try {
          await supabase.storage.from('bucket').remove([tempStoragePath]);
        } catch (cleanupError) {
          console.warn('Failed to clean up temporary lighting schedule upload:', cleanupError);
        }
      }
      setIsResolvingPostCreateLightingGate(false);
    }
  };

  const handlePostCreateLightingGatePowerOnly = async () => {
    if (!postCreateLightingGate || !supabase) return;
    setIsResolvingPostCreateLightingGate(true);

    try {
      const { error } = await supabase
        .from('estimations')
        .update({
          takeoff_scope: 'power_only',
          setup_gate_status: 'ready',
        })
        .eq('id', postCreateLightingGate.projectId);
      if (error) throw error;

      setProjects((prev) => prev.map((project) => (
        project.id === postCreateLightingGate.projectId
          ? { ...project, setupGateStatus: 'ready', takeoffScope: 'power_only' }
          : project
      )));

      setPostCreateLightingGate(null);
      setPostCreateLightingScheduleFile(null);
      setPostCreateLightingGateError(null);
      setIsDraggingPostCreateLightingSchedule(false);
      navigate(`/estimation/${postCreateLightingGate.projectId}`);
      setSearchQuery('');
    } catch (error) {
      console.error('Failed to continue with power-only takeoff:', error);
      alert('Failed to continue with power-only setup. Please try again.');
    } finally {
      setIsResolvingPostCreateLightingGate(false);
    }
  };

  useEffect(() => {
    if (!supabase || displayProjects.length === 0) return;

    const preloadFiles = async () => {
      const projectsToLoad = displayProjects.filter(
        (project) => !projectFilesCache[project.id]
      );

      if (projectsToLoad.length === 0) return;

      try {
        const results = await Promise.all(
          projectsToLoad.map(async (project) => {
            const files = await fetchProjectFiles(project.id);
            return { projectId: project.id, ...files };
          })
        );

        setProjectFilesCache((prev) => {
          const updated = { ...prev };
          results.forEach(({ projectId, blueprints, specs }) => {
            updated[projectId] = { blueprints, specs };
          });
          return updated;
        });
      } catch (error) {
        console.error('Error preloading project files:', error);
      }
    };

    preloadFiles();
  }, [displayProjects, supabase, projectFilesCache]);

  // Toggle expand/collapse for project editing
  const handleToggleExpand = async (projectId: string) => {
    if (expandedProjectId === projectId) {
      // Collapse
      setExpandedProjectId(null);
      setBlueprintFiles([]);
      setSpecsFilesForManager([]);
      setEditingProjectNameId(null);
      setEditingProjectName('');
      setFileNameEdits({});
      setPendingDrawingFiles([]);
      setPendingDrawingDeletions(new Set());
      setPendingSpecsFiles([]);
      setPendingSpecsDeletions(new Set());
      setIsSavingChanges(false);
    } else {
      // Expand using cached data
      let cacheEntry = projectFilesCache[projectId];

      // Fallback fetch if not cached yet
      if (!cacheEntry) {
        const files = await fetchProjectFiles(projectId);
        cacheEntry = files;
        setProjectFilesCache((prev) => ({
          ...prev,
          [projectId]: files,
        }));
      }

      const bpList = cacheEntry.blueprints || [];
      const specsList = cacheEntry.specs || [];
      setBlueprintFiles(bpList);
      setSpecsFilesForManager(specsList);
      const project = allProjects.find((p: any) => p.id === projectId);
      setEditingProjectName(project?.name || '');
      setEditingProjectNameId(null);
      setPendingDrawingFiles([]);
      setPendingDrawingDeletions(new Set());
      setPendingSpecsFiles([]);
      setPendingSpecsDeletions(new Set());
      setFileNameEdits(buildFileNameEditMap(bpList, specsList));
      setIsSavingChanges(false);
      setExpandedProjectId(projectId);
    }
  };

  // Update project name
  const handleUpdateProjectName = async (projectId: string) => {
    if (!editingProjectName.trim()) return;

    try {
      if (!supabase) return;
      
      const { error } = await supabase
        .from('estimations')
        .update({ name: editingProjectName.trim() })
        .eq('id', projectId);

      if (error) throw error;
      
      await updateProject(projectId, { name: editingProjectName.trim() });
      setEditingProjectName(editingProjectName.trim());
      
      // Refresh project list
      window.dispatchEvent(new Event('refetch-projects'));
    } catch (error) {
      console.error('Error updating project name:', error);
      alert('Failed to update project name');
    }
  };

  // Add drawing files (pending)
  const handleAddDrawingFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const uploads = Array.from(e.target.files).map((file, index) => {
      const tempId = `pending-drawing-${Date.now()}-${index}`;
      return { file, tempId };
    });
    setPendingDrawingFiles((prev) => [...prev, ...uploads]);
    setFileNameEdits((prev) => {
      const updated = { ...prev };
      uploads.forEach(({ file, tempId }) => {
    const { base } = splitFileName(file.name);
        updated[tempId] = base;
      });
      return updated;
    });
  };

  const handleRemovePendingDrawingFile = (tempId: string) => {
    setPendingDrawingFiles((prev) => prev.filter((entry) => entry.tempId !== tempId));
    setFileNameEdits((prev) => {
      const updated = { ...prev };
      delete updated[tempId];
      return updated;
    });
  };

  const handleToggleDeleteDrawingFile = (fileId: string) => {
    setPendingDrawingDeletions((prev) => {
      const updated = new Set(prev);
      if (updated.has(fileId)) {
        updated.delete(fileId);
      } else {
        updated.add(fileId);
      }
      return updated;
    });
  };

  // Add specs files
  const handleAddSpecsFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const uploads = Array.from(e.target.files).map((file, index) => {
      const tempId = `pending-spec-${Date.now()}-${index}`;
      return { file, tempId };
    });
    setPendingSpecsFiles((prev) => [...prev, ...uploads]);
    setFileNameEdits((prev) => {
      const updated = { ...prev };
      uploads.forEach(({ file, tempId }) => {
        const { base } = splitFileName(file.name);
        updated[tempId] = base;
      });
      return updated;
    });
  };

  // Delete specs file
  const hasPendingFileChanges = () => {
    const drawingsRenamed = blueprintFiles.some(
      (file) =>
        !pendingDrawingDeletions.has(file.id) &&
        getEditedFileName(file.id, file.file_name, fileNameEdits) !== file.file_name
    );
    const specsRenamed = specsFilesForManager.some(
      (file) =>
        !pendingSpecsDeletions.has(file.id) &&
        getEditedFileName(file.id, file.file_name, fileNameEdits) !== file.file_name
    );

    const editProject = expandedProjectId ? allProjects.find((p: any) => p.id === expandedProjectId) : null;
    const nameChanged = editProject ? editingProjectName.trim() !== '' && editingProjectName.trim() !== editProject.name : false;

    return (
      pendingDrawingFiles.length > 0 ||
      pendingDrawingDeletions.size > 0 ||
      drawingsRenamed ||
      pendingSpecsFiles.length > 0 ||
      pendingSpecsDeletions.size > 0 ||
      specsRenamed ||
      nameChanged
    );
  };

  const handleCancelFileChanges = () => {
    if (isSavingChanges) return; // Don't allow cancel while saving
    
    // Close the dropdown
    setExpandedProjectId(null);
    setBlueprintFiles([]);
    setSpecsFilesForManager([]);
    setEditingProjectNameId(null);
    setEditingProjectName('');
    setFileNameEdits({});
    setPendingDrawingFiles([]);
    setPendingDrawingDeletions(new Set());
    setPendingSpecsFiles([]);
    setPendingSpecsDeletions(new Set());
  };

  const handleSaveFileChanges = async (projectId: string) => {
    if (!supabase || !hasPendingFileChanges()) return;

    setIsSavingChanges(true);

    try {
      // Save project name if changed
      const editProject = allProjects.find((p: any) => p.id === projectId);
      if (editProject && editingProjectName.trim() && editingProjectName.trim() !== editProject.name) {
        await supabase
          .from('estimations')
          .update({ name: editingProjectName.trim() })
          .eq('id', projectId);
        await updateProject(projectId, { name: editingProjectName.trim() });
      }

      // Upload pending drawings — merge into the existing drawing PDF
      if (pendingDrawingFiles.length > 0) {
        // Step 1: Upload each new file to temporary storage
        const uploadedTempFiles: { storagePath: string; fileName: string }[] = [];

        for (const entry of pendingDrawingFiles) {
          const file = entry.file;
          console.log(`📤 Uploading temp drawing ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);

          setIsUploading(true);
          setUploadingFileName(file.name);
          setUploadProgress(0);

          const { ext } = splitFileName(file.name);
          const editedName = `${(fileNameEdits[entry.tempId] || splitFileName(file.name).base).trim() || splitFileName(file.name).base}${ext}`;
          const timestamp = Date.now();
          const filePath = `blueprints/temp_${timestamp}_${Math.random().toString(36).substring(7)}${ext}`;

          let uploadSuccess = false;

          if (shouldUseResumableUpload(file.size)) {
            console.log('📤 Using TUS resumable upload...');
            const result = await resumableUpload({
              bucketName: 'bucket',
              filePath,
              file,
              onProgress: (progress) => {
                setUploadProgress(progress.percentage);
                console.log(`   Progress: ${progress.percentage}%`);
              },
              onError: (error) => {
                console.error(`   Upload error:`, error);
                setIsUploading(false);
              }
            });
            uploadSuccess = result.success;
          } else {
            const { error: uploadError } = await supabase.storage
              .from('bucket')
              .upload(filePath, file);
            uploadSuccess = !uploadError;
          }

          setIsUploading(false);

          if (uploadSuccess) {
            uploadedTempFiles.push({ storagePath: filePath, fileName: editedName });
          }
        }

        // Step 2: Call backend to merge new pages into the existing drawing
        if (uploadedTempFiles.length > 0) {
          setIsMergingPDFs(true);
          setMergeProgress(50);

          const existingFile = blueprintFiles.find(f => !pendingDrawingDeletions.has(f.id));

          console.log(`🔄 Merging ${uploadedTempFiles.length} new file(s) into existing drawing...`);
          const mergeResponse = await fetch('/api/merge-drawing', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              estimationId: projectId,
              existingFileId: existingFile?.id || null,
              newFiles: uploadedTempFiles,
            }),
          });

          if (!mergeResponse.ok) {
            const err = await mergeResponse.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to merge drawings');
          }

          const mergeResult = await mergeResponse.json();
          console.log(`✅ Merge complete: ${mergeResult.pageCount} pages, ${(mergeResult.fileSize / 1024 / 1024).toFixed(2)} MB`);

          setMergeProgress(100);
          setIsMergingPDFs(false);
        }
      }

      // Delete drawings marked for removal
      if (pendingDrawingDeletions.size > 0) {
        const drawingsToDelete = blueprintFiles.filter((file) => pendingDrawingDeletions.has(file.id));
        for (const file of drawingsToDelete) {
          await supabase.storage.from('bucket').remove([file.storage_path]);
          await supabase.from('estimation_files').delete().eq('id', file.id);
        }
      }

      // Rename existing drawings
      for (const file of blueprintFiles) {
        if (pendingDrawingDeletions.has(file.id)) continue;
        const finalName = getEditedFileName(file.id, file.file_name, fileNameEdits);
        if (finalName !== file.file_name) {
          await supabase
            .from('estimation_files')
            .update({ file_name: finalName })
            .eq('id', file.id);
        }
      }

      // Upload pending specs
      if (pendingSpecsFiles.length > 0) {
        const { data: existingFiles } = await supabase
          .from('estimation_specs')
          .select('file_order')
          .eq('estimation_id', projectId)
          .order('file_order', { ascending: false })
          .limit(1);

        let nextOrder = existingFiles && existingFiles.length > 0 ? existingFiles[0].file_order + 1 : 0;

        for (const entry of pendingSpecsFiles) {
          const file = entry.file;
          console.log(`🔍 DEBUG: Uploading pending spec ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
          
          setIsUploading(true);
          setUploadingFileName(file.name);
          setUploadProgress(0);
          
          const { ext } = splitFileName(file.name);
          const editedName = `${(fileNameEdits[entry.tempId] || splitFileName(file.name).base).trim() || splitFileName(file.name).base}${ext}`;
          const timestamp = Date.now();
          const filePath = `specs/${timestamp}_${Math.random().toString(36).substring(7)}${ext}`;

          let uploadSuccess = false;
          
          if (shouldUseResumableUpload(file.size)) {
            console.log(`📤 Using TUS resumable upload for ${file.name}...`);
            const result = await resumableUpload({
              bucketName: 'bucket',
              filePath,
              file,
              onProgress: (progress) => {
                setUploadProgress(progress.percentage);
                console.log(`   ${file.name} progress: ${progress.percentage}%`);
              },
              onError: (error) => {
                console.error(`   Upload error:`, error);
                setIsUploading(false);
              }
            });
            uploadSuccess = result.success;
          } else {
            const { error: uploadError } = await supabase.storage
              .from('bucket')
              .upload(filePath, file);
            uploadSuccess = !uploadError;
          }
          
          setIsUploading(false);
          
          if (!uploadSuccess) continue;

          await supabase.from('estimation_specs').insert({
            estimation_id: projectId,
            file_name: editedName,
            file_size: file.size,
            file_type: file.type,
            storage_path: filePath,
            file_order: nextOrder,
          });
          nextOrder += 1;
        }
      }

      // Delete marked specs
      if (pendingSpecsDeletions.size > 0) {
        const toDelete = specsFilesForManager.filter((file) => pendingSpecsDeletions.has(file.id));
        for (const file of toDelete) {
          await supabase.storage.from('bucket').remove([file.storage_path]);
          await supabase.from('estimation_specs').delete().eq('id', file.id);
        }
      }

      // Rename existing specs
      for (const file of specsFilesForManager) {
        if (pendingSpecsDeletions.has(file.id)) continue;
        const finalName = getEditedFileName(file.id, file.file_name, fileNameEdits);
        if (finalName !== file.file_name) {
          await supabase
            .from('estimation_specs')
            .update({ file_name: finalName })
            .eq('id', file.id);
        }
      }

      const refreshed = await fetchProjectFiles(projectId);
      setBlueprintFiles(refreshed.blueprints || []);
      setSpecsFilesForManager(refreshed.specs || []);
      setProjectFilesCache((prev) => ({
        ...prev,
        [projectId]: refreshed,
      }));
      setPendingDrawingFiles([]);
      setPendingDrawingDeletions(new Set());
      setPendingSpecsFiles([]);
      setPendingSpecsDeletions(new Set());
      setFileNameEdits(buildFileNameEditMap(refreshed.blueprints || [], refreshed.specs || []));
      setIsSavingChanges(false);
      
      // Close the expanded section after successful save
      setExpandedProjectId(null);
      setBlueprintFiles([]);
      setSpecsFilesForManager([]);
      setEditingProjectNameId(null);
      setEditingProjectName('');
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Failed to save file changes');
      setIsSavingChanges(false);
    }
  };
  const handleRemovePendingSpecsFile = (tempId: string) => {
    setPendingSpecsFiles((prev) => prev.filter((entry) => entry.tempId !== tempId));
    setFileNameEdits((prev) => {
      const updated = { ...prev };
      delete updated[tempId];
      return updated;
    });
  };

  const handleToggleDeleteSpecsFile = (fileId: string) => {
    setPendingSpecsDeletions((prev) => {
      const updated = new Set(prev);
      if (updated.has(fileId)) {
        updated.delete(fileId);
      } else {
        updated.add(fileId);
      }
      return updated;
    });
  };

  const handleFileNameChange = (fileId: string, value: string) => {
    setFileNameEdits((prev) => ({
      ...prev,
      [fileId]: value,
    }));
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrawingDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingDrawing(true);
  };

  const handleDrawingDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingDrawing(false);
  };

  const handleDrawingDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingDrawing(false);

    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
    
    const uploads = Array.from(e.dataTransfer.files).map((file, index) => {
      const tempId = `pending-drawing-${Date.now()}-${index}`;
      return { file, tempId };
    });
    setPendingDrawingFiles((prev) => [...prev, ...uploads]);
    setFileNameEdits((prev) => {
      const updated = { ...prev };
      uploads.forEach(({ file, tempId }) => {
        const { base } = splitFileName(file.name);
        updated[tempId] = base;
      });
      return updated;
    });
  };

  const handleSpecsDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingSpecs(true);
  };

  const handleSpecsDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingSpecs(false);
  };

  const handleSpecsDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingSpecs(false);

    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
    
    const uploads = Array.from(e.dataTransfer.files).map((file, index) => {
      const tempId = `pending-spec-${Date.now()}-${index}`;
      return { file, tempId };
    });
    setPendingSpecsFiles((prev) => [...prev, ...uploads]);
    setFileNameEdits((prev) => {
      const updated = { ...prev };
      uploads.forEach(({ file, tempId }) => {
        const { base } = splitFileName(file.name);
        updated[tempId] = base;
      });
      return updated;
    });
  };

  const handleModalDrawingDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModalDrawing(true);
  };

  const handleModalDrawingDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModalDrawing(false);
  };

  const handleModalDrawingDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModalDrawing(false);

    const files = Array.from(e.dataTransfer.files);
    addDrawingFiles(files);
  };

  const handleModalSpecsDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModalSpecs(true);
  };

  const handleModalSpecsDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModalSpecs(false);
  };

  const handleModalSpecsDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModalSpecs(false);

    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
    
    const newFiles = Array.from(e.dataTransfer.files);
    // Check for duplicates
    const existingNames = newSpecsFiles.map(f => f.name.toLowerCase());
    const duplicates: string[] = [];
    const uniqueFiles = newFiles.filter(file => {
      if (existingNames.includes(file.name.toLowerCase())) {
        duplicates.push(file.name);
        return false;
      }
      return true;
    });
    
    if (duplicates.length > 0) {
      alert(`The following files already exist and were skipped:\n${duplicates.join('\n')}`);
    }
    
    setNewSpecsFiles(prev => [...prev, ...uniqueFiles]);
  };

  const pendingChanges = hasPendingFileChanges();

  if (billingGateLoading) {
    return (
      <div className="billing-gate-loading-screen">
        <div className="billing-gate-loading-card">
          <img
            src={theme === 'dark' ? '/bidflowlogo-removebg-preview.png' : '/output-onlinepngtools.png'}
            alt="Bidflow"
            className="billing-gate-loading-logo"
          />
          <div className="billing-gate-loading-spinner" aria-hidden="true" />
          <h2 className="billing-gate-loading-title">Checking billing status...</h2>
          <p className="billing-gate-loading-subtitle">
            Hang tight while we verify your card setup.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`home-page-container ${showRunningJobsSidebar ? 'running-sidebar-open' : 'running-sidebar-closed'}`}>
      {/* Top Bar */}
      <div className="home-top-bar">
        <div className="home-search-section">
          <div className="home-search-box">
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className={`filter-toggle-btn ${showSortDropdown ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowSortDropdown(prev => !prev);
              }}
            >
              <SlidersHorizontal size={18} />
            </button>
            {showSortDropdown && (
            <div className="filter-dropdown" onClick={(e) => e.stopPropagation()}>
              <div className="filter-dropdown-header">Sort by</div>
              <div className="filter-dropdown-group">
                {([
                  { field: 'lastOpened' as const, label: 'Last Opened' },
                  { field: 'dateCreated' as const, label: 'Date Created' },
                  { field: 'name' as const, label: 'Name' },
                ]).map(({ field, label }) => (
                  <button
                    key={field}
                    className={`filter-option ${sortBy === field ? 'active' : ''}`}
                    onClick={() => {
                      if (sortBy === field) {
                        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortBy(field);
                        setSortDirection(field === 'name' ? 'asc' : 'desc');
                      }
                    }}
                  >
                    <span className="filter-option-label">{label}</span>
                    {sortBy === field && (
                      <span className="filter-option-direction">
                        {field === 'name'
                          ? (sortDirection === 'asc' ? 'A → Z' : 'Z → A')
                          : (sortDirection === 'desc' ? 'Newest first' : 'Oldest first')
                        }
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <div className="filter-dropdown-footer">
                {displayProjects.length} project{displayProjects.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>

      <aside
        className={`running-jobs-sidebar-shell ${showRunningJobsSidebar ? 'open' : 'closed'}`}
        aria-label="Running takeoffs sidebar"
      >
        <svg
          aria-hidden="true"
          focusable="false"
          width="0"
          height="0"
          style={{ position: 'absolute', width: 0, height: 0 }}
        >
          <defs>
            <linearGradient id="running-jobs-icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a78bfa" />
              <stop offset="50%" stopColor="#8b5cf6" />
              <stop offset="100%" stopColor="#6d28d9" />
            </linearGradient>
          </defs>
        </svg>
        {!showRunningJobsSidebar && (
          <button
            type="button"
            className="running-jobs-collapsed-opener"
            onClick={() => setShowRunningJobsSidebar(true)}
            aria-label="See running takeoffs"
          >
            <Rocket size={16} strokeWidth={2.2} fill="none" strokeLinejoin="round" />
            {runningJobs.length > 0 && (
              <span
                className="running-jobs-collapsed-count"
                aria-label={`${runningJobs.length} running takeoff${runningJobs.length === 1 ? '' : 's'}`}
              >
                {runningJobs.length}
              </span>
            )}
            <span className="running-jobs-collapsed-tooltip" role="tooltip">
              See running takeoffs
            </span>
          </button>
        )}

        <div className="running-jobs-sidebar" aria-hidden={!showRunningJobsSidebar}>
          <div className="running-jobs-header">
            <h3>
              <span className="running-jobs-header-icon" aria-hidden="true">
                <Rocket size={16} strokeWidth={2.2} fill="none" strokeLinejoin="round" />
              </span>
              Running Takeoffs
            </h3>
            <button
              type="button"
              className="running-jobs-header-toggle"
              onClick={() => setShowRunningJobsSidebar(false)}
              aria-label="Collapse running takeoffs sidebar"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {runningJobs.length === 0 ? (
            <div className="running-jobs-empty">No takeoffs are currently running.</div>
          ) : (
            <div className="running-jobs-list">
              {runningJobs.map((project) => {
                const runStageVisual = getRunStageVisual(project.runStage);
                return (
                <div key={project.id} className={`running-job-row stage-${runStageVisual}`}>
                  <div className="running-job-name">{capitalizeWords(project.name)}</div>
                  <div className="running-job-meta">
                    {renderRunStageBadge(project.runStage)}
                    {!isLabelingStage(project.runStage) && (
                      <span className="running-job-time">
                        ETA {formatTimeLeft(getLiveTimeLeft(project))}
                      </span>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </aside>

      {/* Projects Section */}
      <div className="home-projects-section">
        <div className="home-projects-grid">
          <div
            className={`project-card-create${createDisabled ? ' project-card-disabled' : ''}`}
            onClick={() => {
              if (createDisabled) {
                handleBillingLockedAction();
                return;
              }
              setShowCreateModal(true);
            }}
            title={createTooltip}
          >
            <Plus size={30} strokeWidth={2.4} />
            <span>Create New Project</span>
          </div>

          {displayProjects.length === 0 && (
            <div className="projects-empty-state">
              <FileText className="empty-icon" />
              <p className="empty-text">No projects found</p>
            </div>
          )}

          {displayProjects.map((project) => {
            const locked = isProjectLocked(project);
            const freshlyDone = isProjectFreshlyDone(project);
            const runStageVisual = getRunStageVisual(project.runStage);
            return (
            <div
              className={`project-card${!hasActiveBilling ? ' project-card-disabled' : ''}${locked ? ' project-card-running' : ''}${freshlyDone ? ' project-card-finished' : ''}`}
              key={project.id}
              onClick={() => {
                if (!locked) {
                  handleOpenProject(project.id);
                }
              }}
              title={
                !hasActiveBilling
                  ? 'You need to connect a card first'
                  : locked
                    ? 'This project is still running'
                    : freshlyDone
                      ? 'Click to open your finished project'
                      : 'Open project'
              }
            >
              <div className="project-card-top">
                <div className="project-icon">
                  <FileText size={24} />
                </div>
                <div className="project-card-name">
                  {capitalizeWords(project.name)}
                </div>
                {locked && (
                  <div className={`project-running-pill stage-${runStageVisual}`}>
                    {isLabelingStage(project.runStage)
                      ? renderRunStageBadge(project.runStage)
                      : (
                        <>
                          {renderRunStageBadge(project.runStage)}
                          <span className="project-running-pill-separator">•</span>
                          <span className="project-running-pill-time">
                            ETA {formatTimeLeft(getLiveTimeLeft(project))}
                          </span>
                        </>
                      )}
                  </div>
                )}
                <div className="project-timestamps">
                  <span className="project-timestamp">
                    <Clock size={11} />
                    Opened {formatTimestamp(project.lastOpenedAt || project.updatedAt)}
                  </span>
                  <span className="project-timestamp">
                    <Clock size={11} />
                    Created {formatTimestamp(project.createdAt)}
                  </span>
                </div>
              </div>

              <div className="project-card-bottom">
                <div className="project-status-wrapper">
                  {freshlyDone ? (
                    <div
                      className="project-status-click-to-open"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenProject(project.id);
                      }}
                      title="Open your finished project"
                    >
                      <Sparkles size={12} strokeWidth={2.4} />
                      <span>Click to open</span>
                    </div>
                  ) : (
                    <div
                      className={`project-status-badge ${locked ? '' : 'clickable'}`.trim()}
                      style={{
                        backgroundColor: getStatusOption(project.status).bgColor,
                        color: getStatusOption(project.status).color,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (locked) return;
                        if (showStatusPicker === project.id) {
                          setShowStatusPicker(null);
                          setStatusPickerRect(null);
                        } else {
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          setStatusPickerRect({ top: rect.top, left: rect.left });
                          setShowStatusPicker(project.id);
                        }
                      }}
                    >
                      {getStatusOption(project.status).label}
                      <ChevronDown size={14} style={{ marginLeft: '0.25rem', opacity: 0.7 }} />
                    </div>
                  )}
                </div>

                <div className="project-card-actions">
                  <button
                    className="project-edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleExpand(project.id);
                    }}
                    title="Edit project"
                  >
                    <Edit2 size={15} />
                  </button>
                  <button
                    className="project-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(project.id, project.name);
                    }}
                    title="Delete project"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Status Picker Dropdown (rendered outside cards to avoid clipping) */}
      {showStatusPicker && statusPickerRect && (() => {
        const pickerProject = allProjects.find((p: any) => p.id === showStatusPicker);
        if (!pickerProject) return null;
        return (
          <div
            className="status-picker-dropdown-fixed"
            style={{ top: statusPickerRect.top - 8, left: statusPickerRect.left }}
            onClick={(e) => e.stopPropagation()}
          >
            {statusOptions.map((opt) => (
              <div
                key={opt.value}
                className={`status-picker-option ${pickerProject.status === opt.value ? 'active' : ''}`}
                style={{
                  backgroundColor: pickerProject.status === opt.value ? opt.bgColor : undefined,
                  color: pickerProject.status === opt.value ? opt.color : undefined,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (pickerProject.status !== opt.value) {
                    updateProject(pickerProject.id, { status: opt.value });
                  }
                  setShowStatusPicker(null);
                  setStatusPickerRect(null);
                }}
              >
                <span
                  className="status-picker-dot"
                  style={{ backgroundColor: opt.color }}
                />
                {opt.label}
              </div>
            ))}
          </div>
        );
      })()}

      {postCreateLightingGate && (
        <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div
            className="modal-container"
            style={{ maxWidth: '860px', maxHeight: 'none', overflow: 'visible' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h2 className="modal-title" style={{ margin: 0 }}>Fixture schedule required</h2>
                <div style={{ position: 'relative', width: '24px', height: '22px', flexShrink: 0 }}>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      border: '1.5px solid rgba(250, 204, 21, 0.42)',
                      borderRadius: '4px',
                      background: 'linear-gradient(135deg, rgba(250, 204, 21, 0.15) 0%, rgba(250, 204, 21, 0.08) 100%)',
                      boxShadow: '0 0 8px rgba(250, 204, 21, 0.14)',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: '7px',
                      top: '2px',
                      bottom: '2px',
                      width: '1px',
                      background: 'var(--border-strong)',
                      opacity: 0.9,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: '2px',
                      right: '2px',
                      top: '7px',
                      height: '1px',
                      background: 'var(--border-strong)',
                      opacity: 0.9,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: '2px',
                      right: '2px',
                      top: '13px',
                      height: '1px',
                      background: 'var(--border-strong)',
                      opacity: 0.9,
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      top: '-5px',
                      right: '-2px',
                      color: '#b8860b',
                      fontWeight: 900,
                      fontSize: '18px',
                      lineHeight: 1,
                      textShadow: '0 0 8px rgba(184, 134, 11, 0.28)',
                    }}
                  >
                    *
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-body" style={{ overflowY: 'visible', flex: 'unset', paddingBottom: '1.5rem' }}>
              <div className="form-field">
                <label className="form-label">Lighting Schedule PDF</label>
                <div
                  className="upload-hint"
                  style={{
                    marginBottom: '0.75rem',
                    color: 'var(--text-muted)',
                    fontSize: '0.8125rem',
                    textAlign: 'left',
                    width: '100%',
                    whiteSpace: 'normal',
                  }}
                >
                  Upload a PDF that includes the lighting schedule.
                </div>
                <label
                  className={`file-upload-button ${isDraggingPostCreateLightingSchedule ? 'dragging' : ''}`}
                  onDragOver={handleDragOver}
                  onDragEnter={handlePostCreateLightingScheduleDragEnter}
                  onDragLeave={handlePostCreateLightingScheduleDragLeave}
                  onDrop={handlePostCreateLightingScheduleDrop}
                >
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handlePostCreateLightingScheduleInputChange}
                    style={{ display: 'none' }}
                    disabled={isResolvingPostCreateLightingGate}
                  />
                  <div className="upload-icon-wrapper">
                    <Upload size={40} />
                  </div>
                  <div className="upload-text">Upload a PDF that includes the lighting schedule</div>
                  <div className="upload-hint">PDF only</div>
                </label>

                {postCreateLightingScheduleFile && (
                  <div className="uploaded-files-list">
                    <div className="uploaded-files-header">Selected file (1)</div>
                    <div className="uploaded-file-item">
                      <FileText size={18} className="uploaded-file-icon" />
                      <div className="uploaded-file-info">
                        <div className="uploaded-file-name">{postCreateLightingScheduleFile.name}</div>
                        <div className="uploaded-file-size">
                          {(postCreateLightingScheduleFile.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      <button
                        className="remove-uploaded-file"
                        onClick={() => setPostCreateLightingScheduleFile(null)}
                        disabled={isResolvingPostCreateLightingGate}
                        type="button"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                )}

                {postCreateLightingGateError && (
                  <div
                    style={{
                      marginTop: '10px',
                      color: '#ef4444',
                      fontSize: '13px',
                      fontWeight: 600,
                    }}
                  >
                    {postCreateLightingGateError}
                  </div>
                )}

                <p style={{ margin: '12px 0 0', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.5 }}>
                  Project: <strong>{capitalizeWords(postCreateLightingGate.projectName)}</strong>
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary takeoff-only-power-btn"
                onClick={handlePostCreateLightingGatePowerOnly}
                disabled={isResolvingPostCreateLightingGate}
              >
                <Zap size={16} />
                <span>{isResolvingPostCreateLightingGate ? 'Working...' : 'Takeoff only power'}</span>
              </button>
              <button
                className="btn-primary scan-lighting-schedule-btn"
                onClick={handleScanPostCreateLightingSchedule}
                disabled={isResolvingPostCreateLightingGate || !postCreateLightingScheduleFile}
              >
                <Lightbulb size={16} />
                <span>{isResolvingPostCreateLightingGate ? 'Scanning schedule...' : 'Scan lighting schedule'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {expandedProjectId && (() => {
        const editProject = allProjects.find((p: any) => p.id === expandedProjectId);
        if (!editProject) return null;
        return (
          <div className="modal-overlay" onClick={() => { if (!isSavingChanges) handleToggleExpand(expandedProjectId); }}>
            <div className="modal-container edit-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">Edit Project</h2>
                <button
                  className="modal-close-btn"
                  onClick={() => handleToggleExpand(expandedProjectId)}
                  disabled={isSavingChanges}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <div className="form-field">
                  <label className="form-label">Project Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editingProjectName}
                    onChange={(e) => setEditingProjectName(e.target.value)}
                  />
                </div>

                <div className="file-sections-grid">
                  {/* Drawings Section */}
                  <div className="file-section">
                    <div className="file-section-header">
                      <span>Drawings</span>
                      <span className="file-count">
                        {blueprintFiles.length + pendingDrawingFiles.length}
                      </span>
                    </div>
                    <div className="file-list">
                      {blueprintFiles.length === 0 && pendingDrawingFiles.length === 0 && (
                        <div className="empty-file-state">No drawings uploaded</div>
                      )}
                      {blueprintFiles.map((file) => (
                        <div
                          key={file.id}
                          className={`file-item ${pendingDrawingDeletions.has(file.id) ? 'marked-delete' : ''}`}
                        >
                          <FileText size={16} className="file-item-icon" />
                          <div className="file-item-details">
                            <div className="file-name-edit">
                              <input
                                type="text"
                                value={fileNameEdits[file.id] ?? splitFileName(file.file_name).base}
                                onChange={(e) => handleFileNameChange(file.id, e.target.value)}
                                placeholder="Drawing name"
                                disabled={isSavingChanges || pendingDrawingDeletions.has(file.id)}
                              />
                              <span className="file-extension">{splitFileName(file.file_name).ext}</span>
                            </div>
                            <div className="file-size">
                              {(file.file_size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                          <button
                            type="button"
                            className={`file-remove-btn ${pendingDrawingDeletions.has(file.id) ? 'undo' : ''}`}
                            onClick={() => handleToggleDeleteDrawingFile(file.id)}
                            disabled={isSavingChanges}
                            title={pendingDrawingDeletions.has(file.id) ? 'Undo remove' : 'Remove'}
                          >
                            {pendingDrawingDeletions.has(file.id) ? <RotateCcw size={14} /> : <X size={14} />}
                          </button>
                        </div>
                      ))}
                      {pendingDrawingFiles.map((entry) => (
                        <div key={entry.tempId} className="file-item pending">
                          <FileText size={16} className="file-item-icon" />
                          <div className="file-item-details">
                            <div className="file-name-edit">
                              <input
                                type="text"
                                value={fileNameEdits[entry.tempId] ?? splitFileName(entry.file.name).base}
                                onChange={(e) => handleFileNameChange(entry.tempId, e.target.value)}
                                placeholder="Drawing name"
                                disabled={isSavingChanges}
                              />
                              <span className="file-extension">{splitFileName(entry.file.name).ext}</span>
                            </div>
                            <div className="file-size">
                              {(entry.file.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                          <button
                            type="button"
                            className="file-remove-btn"
                            onClick={() => handleRemovePendingDrawingFile(entry.tempId)}
                            disabled={isSavingChanges}
                            title="Remove pending file"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <label
                      className={`file-upload-area ${isDraggingDrawing ? 'dragging' : ''}`}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDrawingDragEnter}
                      onDragLeave={handleDrawingDragLeave}
                      onDrop={handleDrawingDrop}
                    >
                      <input
                        type="file"
                        accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png"
                        onChange={handleAddDrawingFiles}
                        style={{ display: 'none' }}
                        disabled={isSavingChanges}
                        multiple
                      />
                      <div className="file-upload-icon"><Upload size={20} /></div>
                      <div className="file-upload-text">Drop files or click to upload</div>
                    </label>
                  </div>

                  {/* Specifications Section */}
                  <div className="file-section">
                    <div className="file-section-header">
                      <span>Specifications & Additional Files</span>
                      <span className="file-count">
                        {specsFilesForManager.length + pendingSpecsFiles.length}
                      </span>
                    </div>
                    <div className="file-list">
                      {specsFilesForManager.length === 0 && pendingSpecsFiles.length === 0 && (
                        <div className="empty-file-state">No specifications</div>
                      )}
                      {specsFilesForManager.map((file) => (
                        <div
                          key={file.id}
                          className={`file-item ${pendingSpecsDeletions.has(file.id) ? 'marked-delete' : ''}`}
                        >
                          <FileText size={16} className="file-item-icon" />
                          <div className="file-item-details">
                            <div className="file-name-edit">
                              <input
                                type="text"
                                value={fileNameEdits[file.id] ?? splitFileName(file.file_name).base}
                                onChange={(e) => handleFileNameChange(file.id, e.target.value)}
                                placeholder="File name"
                                disabled={isSavingChanges}
                              />
                              <span className="file-extension">{splitFileName(file.file_name).ext}</span>
                            </div>
                            <div className="file-size">
                              {(file.file_size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                          <button
                            type="button"
                            className={`file-remove-btn ${pendingSpecsDeletions.has(file.id) ? 'undo' : ''}`}
                            onClick={() => handleToggleDeleteSpecsFile(file.id)}
                            disabled={isSavingChanges}
                            title={pendingSpecsDeletions.has(file.id) ? 'Undo remove' : 'Remove'}
                          >
                            {pendingSpecsDeletions.has(file.id) ? <RotateCcw size={14} /> : <X size={14} />}
                          </button>
                        </div>
                      ))}
                      {pendingSpecsFiles.map((entry) => (
                        <div key={entry.tempId} className="file-item pending">
                          <FileText size={16} className="file-item-icon" />
                          <div className="file-item-details">
                            <div className="file-name-edit">
                              <input
                                type="text"
                                value={fileNameEdits[entry.tempId] ?? splitFileName(entry.file.name).base}
                                onChange={(e) => handleFileNameChange(entry.tempId, e.target.value)}
                                placeholder="File name"
                                disabled={isSavingChanges}
                              />
                              <span className="file-extension">{splitFileName(entry.file.name).ext}</span>
                            </div>
                            <div className="file-size">
                              {(entry.file.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                          <button
                            type="button"
                            className="file-remove-btn"
                            onClick={() => handleRemovePendingSpecsFile(entry.tempId)}
                            disabled={isSavingChanges}
                            title="Remove pending file"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <label
                      className={`file-upload-area ${isDraggingSpecs ? 'dragging' : ''}`}
                      onDragOver={handleDragOver}
                      onDragEnter={handleSpecsDragEnter}
                      onDragLeave={handleSpecsDragLeave}
                      onDrop={handleSpecsDrop}
                    >
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.txt"
                        onChange={handleAddSpecsFiles}
                        style={{ display: 'none' }}
                        disabled={isSavingChanges}
                        multiple
                      />
                      <div className="file-upload-icon"><Upload size={20} /></div>
                      <div className="file-upload-text">Drop files or click to upload</div>
                    </label>
                  </div>
                </div>

                <div className="expanded-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCancelFileChanges}
                    disabled={isSavingChanges}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => handleSaveFileChanges(expandedProjectId)}
                    disabled={!pendingChanges || isSavingChanges}
                  >
                    {isSavingChanges ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="modal-overlay create-project-modal-overlay" onClick={handleCancelCreate}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Create New Project</h2>
                    <button
                className="modal-close-btn"
                      onClick={handleCancelCreate}
                      disabled={creatingProject}
                    >
                <X size={20} />
                    </button>
                  </div>

            <div className="modal-body">
              <div className="file-sections-grid-modal">
                <div className="form-field">
                  <label className="form-label">Project Name</label>
                      <input
                        type="text"
                    className="form-input"
                        placeholder="Enter project name..."
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        disabled={creatingProject}
                        autoFocus
                      />
                    </div>

                <div className="form-field">
                  <label className="form-label">Drawings</label>
                  <label
                    className={`file-upload-button ${isDraggingModalDrawing ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleModalDrawingDragEnter}
                    onDragLeave={handleModalDrawingDragLeave}
                    onDrop={handleModalDrawingDrop}
                  >
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => {
                        if (e.target.files) {
                          addDrawingFiles(Array.from(e.target.files));
                          e.currentTarget.value = '';
                        }
                      }}
                      style={{ display: 'none' }}
                      disabled={creatingProject}
                      multiple
                    />
                    <div className="upload-icon-wrapper">
                      <Upload size={40} />
                    </div>
                    <div className="upload-text">Upload drawing PDFs</div>
                    <div className="upload-hint">Drop files here or click to browse</div>
                  </label>
                  {newProjectFiles.length > 0 && (
                    <div className="uploaded-files-list">
                      <div className="uploaded-files-header">Selected files ({newProjectFiles.length})</div>
                      {newProjectFiles.map((file, index) => (
                        <div key={index} className="uploaded-file-item">
                          <div className="uploaded-file-thumbnail-wrapper">
                            {newProjectFileThumbnails[getDrawingFileKey(file)] ? (
                              <img
                                src={newProjectFileThumbnails[getDrawingFileKey(file)]}
                                alt={`${file.name} first-page thumbnail`}
                                className="uploaded-file-thumbnail"
                              />
                            ) : (
                              <div className="uploaded-file-thumbnail-fallback">
                                <FileText size={18} className="uploaded-file-icon" />
                              </div>
                            )}
                          </div>
                          <div className="uploaded-file-info">
                            <div className="uploaded-file-name">{file.name}</div>
                            <div className="uploaded-file-size">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                          <button
                            className="remove-uploaded-file"
                            onClick={() => removeDrawingFile(index)}
                            disabled={creatingProject}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* <div className="form-field">
                  <label className="form-label">
                    Specifications <span className="form-label-optional">(Optional)</span>
                  </label>
                  <div className="upload-hint" style={{ marginBottom: '0.75rem', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                    Upload spec docs used for AI specification search
                  </div>

                  {newSpecsFiles.length > 0 && (
                    <div className="uploaded-files-list">
                      {newSpecsFiles.map((file, index) => (
                        <div key={index} className="uploaded-file-item">
                          <FileText size={18} className="uploaded-file-icon" />
                          <div className="uploaded-file-info">
                            <div className="uploaded-file-name">{file.name}</div>
                            <div className="uploaded-file-size">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                          <button
                            className="remove-uploaded-file"
                            onClick={() => removeSpecsFile(index)}
                            disabled={creatingProject}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <label
                    className={`file-upload-button ${isDraggingModalSpecs ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleModalSpecsDragEnter}
                    onDragLeave={handleModalSpecsDragLeave}
                    onDrop={handleModalSpecsDrop}
                  >
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={handleSpecsFileChange}
                      style={{ display: 'none' }}
                      disabled={creatingProject}
                      multiple
                    />
                    <div className="upload-icon-wrapper">
                      <Upload size={40} />
                    </div>
                    <div className="upload-text">
                      {newSpecsFiles.length > 0
                        ? `${newSpecsFiles.length} file${newSpecsFiles.length > 1 ? 's' : ''} selected - Drop or click to add more`
                        : 'Drop files or click to upload specifications'}
                    </div>
                    <div className="upload-hint">PDF, DOC, DOCX, TXT</div>
                  </label>
                </div> */}

                            </div>
                    </div>

            <div className="modal-footer">
              {/* Merging Progress Bar */}
              {isMergingPDFs && (
                <div style={{
                  width: '100%',
                  marginBottom: '1rem',
                  padding: '0 1rem'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-muted)'
                  }}>
                    <span>Merging {newProjectFiles.length} PDF files...</span>
                    <span style={{ fontWeight: '600', color: 'var(--cyan-text-dark)' }}>{mergeProgress.toFixed(1)}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: 'var(--border-color)',
                    borderRadius: '9999px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      backgroundColor: 'var(--cyan-text-dark)',
                      borderRadius: '9999px',
                      transition: 'width 0.3s ease',
                      width: `${mergeProgress}%`
                    }} />
                  </div>
                </div>
              )}
              
              {/* Upload Progress Bar */}
              {isUploading && (
                <div style={{
                  width: '100%',
                  marginBottom: '1rem',
                  padding: '0 1rem'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-muted)'
                  }}>
                    <span>Uploading {uploadingFileName}...</span>
                    <span style={{ fontWeight: '600', color: 'var(--cyan-text-dark)' }}>{uploadProgress.toFixed(1)}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: 'var(--border-color)',
                    borderRadius: '9999px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      backgroundImage: 'linear-gradient(90deg, #7dd3fc 0%, #38bdf8 45%, #0ea5e9 70%, #0369a1 100%)',
                      borderRadius: '9999px',
                      transition: 'width 0.3s ease',
                      boxShadow: '0 0 8px rgba(56, 189, 248, 0.45)',
                      width: `${uploadProgress}%`
                    }} />
                  </div>
                </div>
              )}
              
                      <button
                className="btn-secondary"
                        onClick={handleCancelCreate}
                        disabled={creatingProject}
                      >
                        Cancel
                      </button>
                      <button
                className="btn-primary"
                        onClick={handleCreateProject}
                disabled={creatingProject || !newProjectName.trim() || newProjectFiles.length === 0 || isMergingPDFs}
                      >
                        {isMergingPDFs ? (
                          'Merging...'
                        ) : creatingProject ? (
                  newProjectFiles.length > 0
                    ? 'Uploading...'
                    : 'Creating...'
                ) : 'Create Project'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
    </div>
  );
}

export default HomePage;
